import { ArrowLeft, Save } from 'lucide-react';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { formatManagementServicePlan } from '../../components/BillingManagement/ManagementServicePlanPolicy';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const UnitAdd = () => {
  const navigate = useNavigate();
  const { buildingId } = useParams();
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedBuildingId, setSelectedBuildingId] = useState(buildingId);

  // 요금플랜 목록 조회 (RMC 사용자만)
  const { data: servicePlansData, isLoading: plansLoading } = useQuery(
    'management-service-plans',
    async () => {
      const res = await api.get('/management-service-billing/plans');
      return res.plans || res || [];
    },
    {
      enabled: currentUser?.role === 'rental_management_company' || currentUser?.role === 'rmc_admin' || currentUser?.role === 'rmc_staff',
      onError: (error) => {
        console.error('요금플랜 조회 실패:', error);
      }
    }
  );

  const servicePlans = servicePlansData || [];

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm();

  // 건물 목록 조회
  const { data: buildingsData, isLoading: buildingsLoading } = useQuery(
    'buildings',
    () => api.get('/buildings'),
    {
      onSuccess: (data) => {
        if (!selectedBuildingId && data.buildings && data.buildings.length > 0) {
          setSelectedBuildingId(data.buildings[0].id);
        }
      }
    }
  );

  const buildings = buildingsData?.buildings || [];

  // 세대 추가 뮤테이션
  const addUnitMutation = useMutation(
    (data) => api.post(`/buildings/${selectedBuildingId}/units`, data),
    {
      onSuccess: () => {
        toast.success('세대가 성공적으로 추가되었습니다.');
        queryClient.invalidateQueries(['units', selectedBuildingId]);
        navigate(`/buildings/${selectedBuildingId}/units`);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '세대 추가에 실패했습니다.');
        setIsSubmitting(false);
      }
    }
  );

  const onSubmit = (data) => {
    if (!selectedBuildingId) {
      toast.error('건물을 선택해주세요.');
      return;
    }
    setIsSubmitting(true);
    console.log('세대 추가 데이터:', data);
    addUnitMutation.mutate(data);
  };

  if (buildingsLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* 헤더 */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <button
            onClick={() => navigate('/buildings')}
            className="mr-4 p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">세대 추가</h1>
            <p className="mt-1 text-sm text-gray-500">
              새로운 세대를 등록하세요
            </p>
          </div>
        </div>
      </div>

      {/* 세대 추가 폼 */}
      <div className="bg-white shadow rounded-lg">
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          {/* 건물 선택 */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">건물 선택</h3>
            <div>
              <label htmlFor="building_id" className="block text-sm font-medium text-gray-700 mb-2">
                건물 선택 *
              </label>
              <select
                id="building_id"
                value={selectedBuildingId || ''}
                onChange={(e) => setSelectedBuildingId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
              >
                <option value="">건물을 선택하세요</option>
                {buildings.map((building) => (
                  <option key={building.id} value={building.id}>
                    {building.name} - {building.address}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* 기본 정보 */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">기본 정보</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* 호수 */}
              <div>
                <label htmlFor="unit_number" className="block text-sm font-medium text-gray-700 mb-2">
                  호수 *
                </label>
                <input
                  type="text"
                  id="unit_number"
                  {...register('unit_number', { required: '호수를 입력해주세요' })}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.unit_number ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="예: 101"
                />
                {errors.unit_number && (
                  <p className="mt-1 text-sm text-red-600">{errors.unit_number.message}</p>
                )}
              </div>

              {/* 층수 */}
              <div>
                <label htmlFor="floor" className="block text-sm font-medium text-gray-700 mb-2">
                  층수
                </label>
                <input
                  type="number"
                  id="floor"
                  {...register('floor', {
                    min: { value: 1, message: '올바른 층수를 입력해주세요' }
                  })}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.floor ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="예: 1"
                />
                {errors.floor && (
                  <p className="mt-1 text-sm text-red-600">{errors.floor.message}</p>
                )}
              </div>

              {/* 면적 */}
              <div>
                <label htmlFor="area" className="block text-sm font-medium text-gray-700 mb-2">
                  면적 (㎡)
                </label>
                <input
                  type="number"
                  step="0.01"
                  id="area"
                  {...register('area', {
                    min: { value: 0, message: '올바른 면적을 입력해주세요' }
                  })}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.area ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="예: 84.5"
                />
                {errors.area && (
                  <p className="mt-1 text-sm text-red-600">{errors.area.message}</p>
                )}
              </div>

              {/* 세대 유형 */}
              <div>
                <label htmlFor="unit_type" className="block text-sm font-medium text-gray-700 mb-2">
                  세대 유형
                </label>
                <select
                  id="unit_type"
                  {...register('unit_type')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">세대 유형 선택</option>
                  <option value="원룸">원룸</option>
                  <option value="1.5룸">1.5룸</option>
                  <option value="투룸">투룸</option>
                  <option value="쓰리룸 이상">쓰리룸 이상</option>
                </select>
              </div>
            </div>
          </div>

          {/* 임대 정보 */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">기준임대료</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* 기준월세 */}
              <div>
                <label htmlFor="monthly_rent" className="block text-sm font-medium text-gray-700 mb-2">
                  기준월세 (원)
                </label>
                <input
                  type="number"
                  id="monthly_rent"
                  {...register('monthly_rent', {
                    min: { value: 0, message: '올바른 금액을 입력해주세요' }
                  })}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.monthly_rent ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="예: 500000"
                />
                {errors.monthly_rent && (
                  <p className="mt-1 text-sm text-red-600">{errors.monthly_rent.message}</p>
                )}
              </div>

              {/* 기준보증금 */}
              <div>
                <label htmlFor="deposit" className="block text-sm font-medium text-gray-700 mb-2">
                  기준보증금 (원)
                </label>
                <input
                  type="number"
                  id="deposit"
                  {...register('deposit', {
                    min: { value: 0, message: '올바른 금액을 입력해주세요' }
                  })}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.deposit ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="예: 10000000"
                />
                {errors.deposit && (
                  <p className="mt-1 text-sm text-red-600">{errors.deposit.message}</p>
                )}
              </div>

              {/* 기준관리비 */}
              <div>
                <label htmlFor="maintenance_fee" className="block text-sm font-medium text-gray-700 mb-2">
                  기준관리비 (원)
                </label>
                <input
                  type="number"
                  id="maintenance_fee"
                  {...register('maintenance_fee', {
                    min: { value: 0, message: '올바른 금액을 입력해주세요' }
                  })}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.maintenance_fee ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="예: 150000"
                />
                {errors.maintenance_fee && (
                  <p className="mt-1 text-sm text-red-600">{errors.maintenance_fee.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* 임대관리수수료 요금플랜 (RMC 사용자만) */}
          {(currentUser?.role === 'rental_management_company' || currentUser?.role === 'rmc_admin' || currentUser?.role === 'rmc_staff') && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">임대관리수수료 요금플랜</h3>
              <div>
                <label htmlFor="service_plan_id" className="block text-sm font-medium text-gray-700 mb-2">
                  요금플랜 선택 <span className="text-xs text-gray-500">(선택사항)</span>
                </label>
                <select
                  id="service_plan_id"
                  {...register('service_plan_id')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  disabled={plansLoading}
                >
                  <option value="">요금플랜 선택 안함</option>
                  {servicePlans.length > 0 ? (
                    servicePlans
                      .filter(plan => plan.is_active)
                      .map(plan => (
                        <option key={plan.id} value={plan.id}>
                          [{plan.plan_category || 'regular'}] {plan.plan_name} ({formatManagementServicePlan(plan)})
                        </option>
                      ))
                  ) : (
                    <option value="" disabled>요금플랜 없음</option>
                  )}
                </select>
                {plansLoading && (
                  <p className="mt-1 text-xs text-blue-600">요금플랜 목록을 불러오는 중...</p>
                )}
                {!plansLoading && servicePlans.length === 0 && (
                  <p className="mt-1 text-xs text-yellow-600">
                    ⚠️ 등록된 요금정책이 없습니다. 먼저 '공통모듈관리' → '임대관리요금정책 설정' 탭에서 요금정책을 생성해주세요.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* 설명 */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              설명
            </label>
            <textarea
              id="description"
              {...register('description')}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="세대에 대한 추가 설명을 입력하세요"
            />
          </div>

          {/* 버튼 */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/buildings')}
              className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="mr-2 h-4 w-4" />
              {isSubmitting ? '저장 중...' : '세대 추가'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UnitAdd;


